import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Users, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';

export default function EquipeLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [schoolLogo, setSchoolLogo] = useState(null);

  useEffect(() => {
    // Load school logo
    const loadLogo = async () => {
      try {
        const res = await api.get('/school-settings');
        if (res.data?.logo) {
          setSchoolLogo(res.data.logo);
        }
      } catch (error) {
        console.log('Logo não disponível');
      }
    };
    loadLogo();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Preencha todos os campos');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/auth/login', { email, password });
      
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('portal_equipe', 'true'); // Flag para identificar acesso pelo portal da equipe
        
        toast.success(`Bem-vindo(a), ${response.data.user.name}!`);
        navigate('/equipe/portal');
      }
    } catch (error) {
      console.error('Erro no login:', error);
      toast.error(error.response?.data?.detail || 'Email ou senha incorretos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center p-4">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
      </div>

      <Card className="w-full max-w-md relative z-10 border-0 shadow-2xl">
        <CardHeader className="text-center pb-2">
          {/* Logo da Escola */}
          <div className="mx-auto mb-4">
            <img 
              src="/novo-dna-logo.png" 
              alt="NOVO DNA Instituto de Aprendizagem" 
              className="h-24 w-auto mx-auto object-contain"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-purple-900">
            Portal da Equipe
          </CardTitle>
          <CardDescription>
            NOVO DNA Instituto de Aprendizagem
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu.email@novodna.com.br"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11"
                data-testid="equipe-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 pr-10"
                  data-testid="equipe-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-11 bg-purple-600 hover:bg-purple-700"
              disabled={loading}
              data-testid="equipe-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Entrando...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Entrar
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t text-center">
            <p className="text-sm text-muted-foreground">
              Acesso exclusivo para colaboradores
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Em caso de problemas, contate a secretaria
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="absolute bottom-4 text-center text-white/60 text-sm">
        <p>© 2026 Novo DNA Instituto de Aprendizagem</p>
      </div>
    </div>
  );
}
